import { Tile, Button, Checkbox } from '@carbon/react';
import { ChevronDown, ChevronUp } from '@carbon/react/icons';
import { useState, useMemo } from 'react';
import { Link, PlainLink } from 'components/link';
import { ReconcilerResult } from './atoms';
import { ComprehensiveMap } from './comprehensive-map';
import { format0, formatNumber } from 'utils/number';
import style from './reconciler.module.scss';

type ReconcilerSummaryProps = {
  result: ReconcilerResult;
  showAllEntityShapes: boolean;
  onShowAllEntityShapesChange: (showAll: boolean) => void;
};

const Stat = ({
  label,
  value,
  type = 'neutral',
}: {
  label: string;
  value: number;
  type?: 'neutral' | 'success' | 'warning' | 'error';
}) => (
  <div className={style.stat}>
    <span className={style.statLabel}>{label}</span>
    <span className={`${style.statValue} ${style[`stat${type}`]}`}>
      {format0(value)}
    </span>
  </div>
);

export const ReconcilerSummary = ({
  result,
  showAllEntityShapes,
  onShowAllEntityShapesChange,
}: ReconcilerSummaryProps) => {
  const [mapOpen, setMapOpen] = useState(false);
  const entityHref = `/entities/${result.entityId}`;
  const documentHref = `/documents/${result.documentId}`;

  // Filter shape geometries based on toggle
  const filteredShapeGeometries = useMemo(() => {
    if (showAllEntityShapes) {
      return result.shapeGeometries;
    }

    const overlappingKeys = result.overlappingShapeKeys ?? [];

    // Create a Set of overlapping shape IDs for fast lookup
    const overlappingShapeIds = new Set(
      overlappingKeys.map((key) => String(key.shapeId))
    );

    // Filter to only include shapes that overlap with validator geometries
    return Object.fromEntries(
      Object.entries(result.shapeGeometries).filter(([shapeId]) =>
        overlappingShapeIds.has(shapeId)
      )
    );
  }, [
    result.shapeGeometries,
    result.overlappingShapeKeys,
    showAllEntityShapes,
  ]);

  // Calculate the displayed shape count
  const displayedShapeCount = Object.keys(filteredShapeGeometries).length;

  return (
    <Tile className={style.summaryTile}>
      <div className={style.summaryHeader}>
        <h3>Reconciliation Summary</h3>
        <div className={style.summaryHeaderActions}>
          <Checkbox
            id="show-all-entity-shapes"
            labelText="Show All Entity Title Asset Shapes"
            checked={showAllEntityShapes}
            onChange={(_evt, { checked }: { checked: boolean }) =>
              onShowAllEntityShapesChange(checked)
            }
          />
          <Button
            kind="ghost"
            renderIcon={mapOpen ? ChevronUp : ChevronDown}
            onClick={() => setMapOpen(!mapOpen)}
          >
            {mapOpen ? 'Hide Summary Map' : 'Show Summary Map'}
          </Button>
        </div>
      </div>
      <div className={style.summaryGrid}>
        <div className={style.summaryRow}>
          <span className={style.label}>Entity:</span>
          <Link to={entityHref}>{result.entityName}</Link>
          <span className={style.dim}>(ID: {result.entityId})</span>
        </div>

        <div className={style.summaryRow}>
          <span className={style.label}>Document:</span>
          <PlainLink href={documentHref}>{result.documentRef}</PlainLink>
          <span className={style.dim}>(ID: {result.documentId})</span>
        </div>

        <div className={style.summaryRow}>
          <span className={style.label}>Spatial Overlap Threshold:</span>
          <span>
            {formatNumber(
              result.overlapThreshold * 100,
              2,
              2,
              'decimal',
              false
            )}
            %
          </span>
        </div>

        <div className={style.statsRow}>
          <Stat
            label="Title Validator Input Locations"
            value={Object.keys(result.validatorInputLocations ?? {}).length}
          />
          <Stat
            label="Title Validator Output Shapes"
            value={result.totalGeometries}
          />
          <Stat label="Entity Title Asset Shapes" value={displayedShapeCount} />
          <Stat label="Total Comparisons" value={result.shapePairComparisons} />
          <Stat
            label="Matched Comparisons"
            value={result.shapePairMatchCount}
            type={result.shapePairMatchCount > 0 ? 'success' : 'neutral'}
          />
          <Stat
            label="Mismatched Comparisons"
            value={result.shapePairMismatchCount}
            type={result.shapePairMismatchCount > 0 ? 'error' : 'neutral'}
          />
        </div>

        {mapOpen && (
          <div className={style.mapSection}>
            <ComprehensiveMap
              comparisonResults={result.comparisonResults}
              validatorGeometries={result.validatorGeometries}
              validatorInputLocations={result.validatorInputLocations}
              shapeGeometries={filteredShapeGeometries}
              isOpen={mapOpen}
              onToggle={() => setMapOpen(!mapOpen)}
            />
          </div>
        )}
      </div>
    </Tile>
  );
};
